import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Loader2, Check } from "lucide-react";
import { logEvent } from "@/services/eventLogService";
import { track } from "@/integrations/posthog/client";
import { useAuth } from "@/contexts/AuthContext";
import { PaywallFooterLinks, PaywallUnavailable } from "@/components/subscription/PaywallParts";
import { useRefreshOnResume } from "@/hooks/useRefreshOnResume";
import {
  getMembershipOffering,
  purchaseMembership,
  restorePurchases,
  hasMembershipEntitlement,
  claimPurchase,
  getCustomerInfo,
  resolvePackageTrialOffer,
  describePurchaseError,
  isPurchasePending,
} from "@/services/revenueCatService";
import type { TrialOffer } from "@/lib/trialOffer";
import { ProgressLink } from "@/components/metrics/history/ProgressLink";
import { PROGRESS_PATH } from "@/lib/metricHistoryRoutes";
import { isNewTrial, logAdEvent } from "@/services/adAttributionService";

/**
 * In-app purchase paywall (native only).
 *
 * Replaces the web-checkout button that used to sit behind the subscription gate.
 * Sending a native user to an external checkout breaks App Store rule 3.1.1
 * (anti-steering) and is an automatic rejection, so on iOS/Android the purchase has
 * to happen here, through the store.
 *
 * Apple also requires a subscription paywall to show, before purchase: the price,
 * the billing period, a Restore Purchases action, and links to Terms and Privacy.
 * All five are below — omitting any of them is a common rejection reason.
 */

/** Billing months per RevenueCat package, for the per-month maths. */
const MONTHS_BY_PACKAGE: Record<string, number> = {
  $rc_monthly: 1,
  $rc_six_month: 6,
  $rc_annual: 12,
};

const HEBREW_PERIOD: Record<string, string> = {
  $rc_monthly: "חודשי",
  $rc_six_month: "חצי שנתי",
  $rc_annual: "שנתי",
};

function monthsFor(pkg: PurchasesPackage): number {
  return MONTHS_BY_PACKAGE[pkg.identifier] ?? 1;
}

/**
 * Re-check for the membership entitlement after the store returned success
 * without one.
 *
 * Deliberately short and bounded: the member is standing at the paywall
 * watching a spinner, so this trades a few seconds for the ability to tell
 * "propagated a moment later" apart from "pending, possibly forever". Anything
 * longer belongs in the background, not in front of the buy button.
 */
async function waitForEntitlement(attempts = 5, delayMs = 1500): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    if (hasMembershipEntitlement(await getCustomerInfo())) return true;
  }
  return false;
}

/**
 * A payment the store has not approved yet (Ask to Buy, a deferred payment
 * method) or has not confirmed to us yet. Restore is named because it always
 * re-reads the store; the automatic resume check can miss the first approval.
 */
const PENDING_NOTICE =
  'הרכישה ממתינה לאישור התשלום. אין צורך לשלם שוב. אחרי האישור, לחצו על "שחזור רכישות" כדי לפתוח את הגישה.';

/** Price per month, in the store's own currency, for the "₪122 לחודש" line. */
function perMonthLabel(pkg: PurchasesPackage): string | null {
  const months = monthsFor(pkg);
  if (months <= 1) return null;
  const perMonth = pkg.product.price / months;
  try {
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: pkg.product.currencyCode,
      maximumFractionDigits: 0,
    }).format(perMonth);
  } catch {
    return null;
  }
}

interface IapPaywallProps {
  /** Called once the store confirms the member is entitled. */
  onPurchased?: () => void;
}

export default function IapPaywall({ onPurchased }: IapPaywallProps) {
  /**
   * ONLY for the doctor's-summary link at the foot of this screen (§4.5). On a
   * native build with purchases live, THIS is the screen a lapsed member meets
   * — `SubscriptionGate` returns here and never reaches its own fallbacks — so
   * without this link the summary is unreachable for exactly the member §4.5
   * names, and for the Apple reviewer signed in on an expired demo account.
   * It changes nothing about who is gated or what is sold.
   */
  const navigate = useNavigate();
  const [packages, setPackages] = useState<PurchasesPackage[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** Bumped by "לנסות שוב" on the no-plans screen to re-ask the store. */
  const [offeringAttempt, setOfferingAttempt] = useState(0);
  /**
   * Free-trial offer per package identifier, read from the store.
   *
   * This screen sold a two-week free trial for days without ever mentioning it:
   * the offer lives on the Play base plan, so Google announced it in its own
   * purchase sheet while our paywall still said only "₪147". Buyers met the best
   * part of the offer after deciding, which is the wrong order.
   *
   * Resolved from the store rather than hardcoded, and rendered only where the
   * store actually returned a free phase — Android decides eligibility
   * server-side and degrades silently, so promising a trial we cannot confirm is
   * the one failure worth engineering against (Apple 2.3.1, Google Deceptive
   * Behavior). No answer means no badge, never an assumed trial.
   */
  const [trials, setTrials] = useState<Record<string, TrialOffer>>({});
  /** The offer on the plan the buyer is about to buy — drives the CTA and the disclaimer. */
  const selectedTrial = selectedId ? trials[selectedId] : undefined;
  /**
   * Set by whichever path unlocks first — purchase, restore, or recovery. They can
   * genuinely race (background mid-purchase, resume, and the original promise
   * settles too), and without this one payment would report as two conversions.
   */
  const settledRef = useRef(false);

  /**
   * Ask the store what each plan actually offers, once the plans are known.
   *
   * Deliberately after render rather than blocking it: a slow or failed
   * eligibility check must never keep a buyer from reaching the store sheet, so
   * the paywall shows prices immediately and the trial badge appears when the
   * answer arrives. Failure resolves to no badge, which is the safe direction.
   */
  useEffect(() => {
    if (!packages?.length) return;
    let cancelled = false;

    (async () => {
      const entries = await Promise.all(
        packages.map(async (pkg) => {
          try {
            return [pkg.identifier, await resolvePackageTrialOffer(pkg)] as const;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      setTrials(Object.fromEntries(entries.filter(Boolean) as [string, TrialOffer][]));
    })();

    return () => {
      cancelled = true;
    };
  }, [packages]);

  /**
   * Both call sites pass an inline arrow, so `onPurchased` is a new function every
   * render. Reading it through a ref keeps `recoverEntitlement` referentially
   * stable — otherwise the mount check would re-run, and the resume listener
   * re-subscribe, on every single render, firing a native `getCustomerInfo` bridge
   * call each time.
   */
  const onPurchasedRef = useRef(onPurchased);
  useEffect(() => {
    onPurchasedRef.current = onPurchased;
  });

  // Claim, then pull the server's new status into AuthContext. Without the
  // refresh the session keeps the pre-purchase status, so the bottom nav keeps
  // routing a member who just paid back to this paywall (see
  // refreshSubscriptionStatus). Through a ref for the same stability reason as
  // onPurchasedRef.
  const { refreshSubscriptionStatus } = useAuth();
  const refreshStatusRef = useRef(refreshSubscriptionStatus);
  useEffect(() => {
    refreshStatusRef.current = refreshSubscriptionStatus;
  });
  const claimAndRefresh = useCallback(() => {
    void claimPurchase().then((entitled) => {
      if (entitled) void refreshStatusRef.current();
    });
  }, []);

  /**
   * Rescue a purchase that completed without us hearing about it.
   *
   * `handlePurchase` awaits `Purchases.purchasePackage`, so the whole unlock —
   * `onPurchased`, `claimPurchase`, and every telemetry event — hangs off that one
   * promise resolving in THIS JS context. It doesn't always. The store sheet takes
   * over the screen, and if the app is backgrounded while it is up, or the WebView
   * is reclaimed under memory pressure, the context dies with the promise still
   * pending. The payment goes through at Google; nothing on our side ever runs.
   *
   * Observed live on 2026-08-10: two purchases were made minutes apart, and only
   * the one completed in the foreground produced a single event — the backgrounded
   * one emitted NOTHING to PostHog or `app_event_logs`, so it registered neither as
   * a success nor a failure. It simply wasn't there. Which also means every
   * historical "nobody attempts a purchase" count is a floor, not a measurement.
   *
   * So: ask the store directly, on mount and on every resume. `getCustomerInfo` is
   * the store's own truth and is unaffected by whatever our JS context did or
   * didn't survive; if the entitlement is live, the member has paid and is owed
   * access regardless of which promise died. `claim-iap` re-verifies server-side
   * before granting, so a client that gets this wrong cannot grant itself anything.
   *
   * Deliberately NOT gated on `busy`: the case worth catching is precisely a
   * purchase still "in flight" from a promise that will never settle. Fires at most
   * once per mount, under its own event name so recoveries stay countable and don't
   * inflate the purchase funnel.
   */
  const recoverEntitlement = useCallback(async () => {
    if (settledRef.current) return;
    if (!hasMembershipEntitlement(await getCustomerInfo())) return;
    settledRef.current = true;

    logEvent("info", "feature", "purchase_recovered", {});
    setNotice(null);
    setBusy(false);
    onPurchasedRef.current?.();
    claimAndRefresh();
  }, [claimAndRefresh]);

  useEffect(() => {
    void recoverEntitlement();
  }, [recoverEntitlement]);

  // Every resume, not the hook's 5-minute default: the window this exists to close
  // is "bought, came straight back", which is seconds wide.
  useRefreshOnResume(recoverEntitlement, 0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const offering = await getMembershipOffering();
      if (cancelled) return;
      const pkgs = offering?.availablePackages ?? [];
      setPackages(pkgs);
      // Monthly is the default: App Store visitors have never heard of StayYoung,
      // and ~95% of existing members are on monthly. Which plan leads is driven by
      // the RevenueCat offering, so this can be retested without an app update.
      const monthly = pkgs.find((p) => p.identifier === "$rc_monthly");
      setSelectedId((monthly ?? pkgs[0])?.identifier ?? null);
      setLoading(false);
      // Reaches PostHog already: eventLogService fans every non-`error` event in a
      // product category ("feature" is one) out through track(), attaching
      // `category` as a property. So paywall_viewed / purchase_started /
      // purchase_completed / purchase_cancelled / restore_completed all land in the
      // revenue funnel from these calls alone — do NOT add a second track() beside
      // them, it would double-count conversions. Only `error`-type events need an
      // explicit capture (see purchase_failed below).
      // One view per visit: a retry that still finds nothing is not a new view.
      if (offeringAttempt === 0 || pkgs.length > 0) {
        logEvent("info", "feature", "paywall_viewed", { plans: pkgs.length });
        logAdEvent("paywall_view", { flow: "app" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [offeringAttempt]);

  /** Cheapest per-month rate, used to express the others as a saving. */
  const monthlyBaseline = useMemo(() => {
    const monthly = packages?.find((p) => p.identifier === "$rc_monthly");
    return monthly ? monthly.product.price : null;
  }, [packages]);

  function savingPercent(pkg: PurchasesPackage): number | null {
    if (!monthlyBaseline || monthsFor(pkg) <= 1) return null;
    const perMonth = pkg.product.price / monthsFor(pkg);
    const pct = Math.round((1 - perMonth / monthlyBaseline) * 100);
    return pct > 0 ? pct : null;
  }

  async function handlePurchase() {
    const pkg = packages?.find((p) => p.identifier === selectedId);
    if (!pkg || busy) return;

    setBusy(true);
    setNotice(null);
    logEvent("info", "feature", "purchase_started", { plan: pkg.identifier });

    const result = await purchaseMembership(pkg);

    if (result.ok && hasMembershipEntitlement(result.customerInfo)) {
      // `settledRef` is already set if the resume-recovery beat this promise to it.
      // Same purchase, already reported and already unlocked — don't count it twice.
      if (!settledRef.current) {
        settledRef.current = true;
        logEvent("info", "feature", "purchase_completed", { plan: pkg.identifier });
        if (isNewTrial(result.customerInfo)) logAdEvent("trial_start", { flow: "app" });
        // Open the content immediately, then persist in the background. Access must
        // never wait on our own server — the store has already confirmed payment.
        onPurchased?.();
        claimAndRefresh();
      }
      setBusy(false);
      return;
    }

    if (result.ok) {
      // The store accepted the request but reports no entitlement. That is EITHER
      // ordinary propagation lag (seconds) OR a genuinely pending transaction:
      // Play's deferred payment methods (cash, carrier billing) and ask-to-buy
      // parental approval can take days, or never complete at all. The SDK gives
      // us no way to tell those apart at this pin.
      //
      // So: wait briefly for the entitlement, and unlock ONLY if it appears. The
      // previous behaviour unlocked unconditionally and told the member "התשלום
      // התקבל", which handed full access to purchases that were never paid — and
      // stated something we did not know to be true.
      logEvent("warning", "feature", "purchase_entitlement_lag", { plan: pkg.identifier });
      setNotice("קיבלנו את הבקשה, מאמתים מול החנות…");

      const confirmed = await waitForEntitlement();

      if (confirmed) {
        if (!settledRef.current) {
          settledRef.current = true;
          logEvent("info", "feature", "purchase_completed", { plan: pkg.identifier, delayed: true });
          onPurchased?.();
          claimAndRefresh();
        }
        setNotice(null);
        setBusy(false);
        return;
      }

      // Still unconfirmed. Do not unlock. `claimPurchase` is still worth firing —
      // it is non-fatal, and the entitlement may land between our last poll and
      // the server's own lookup.
      // A resume-recovery may have unlocked this purchase during the wait; it then
      // already reported the outcome, so don't add a second one.
      if (settledRef.current) {
        setNotice(null);
        setBusy(false);
        return;
      }
      // logEvent already forwards non-error events to PostHog; a second track()
      // here counted every pending purchase twice.
      logEvent("warning", "feature", "purchase_pending", { plan: pkg.identifier, source: "entitlement_lag" });
      claimAndRefresh();
      // Resume recovery usually unlocks this on its own, but it reads the SDK's
      // cached customer info and can miss the first resume after approval.
      // Restore always fetches fresh, so the copy names it.
      setNotice(PENDING_NOTICE);
      setBusy(false);
      return;
    }

    // `result.ok === false` rather than a truthiness check: tsconfig is
    // non-strict here, so only the explicit literal comparison narrows the union
    // enough to reach `.cancelled`. Same idiom the failure arm already used.
    if (result.ok === false && result.cancelled === true) {
      // Backing out of Apple's sheet is an ordinary outcome, not a failure. It is
      // logged as its own event so the funnel can tell "changed their mind" apart
      // from "the store broke" — the two have opposite fixes, and folding them
      // together made the paywall look far more broken than it is.
      logEvent("info", "feature", "purchase_cancelled", { plan: pkg.identifier });
    } else if (result.ok === false && isPurchasePending(result.error)) {
      // Ask to Buy or a deferred payment: the store may still take the money, so
      // "not completed" would invite a second payment. Same arm as an accepted
      // purchase that never confirmed, above.
      logEvent("warning", "feature", "purchase_pending", { plan: pkg.identifier, error_code: "20" });
      claimAndRefresh();
      setNotice(PENDING_NOTICE);
    } else if (result.ok === false) {
      // Carry the store's own error code through to both sinks. Diagnosing a failed
      // purchase from `{ plan }` alone is impossible — see describePurchaseError.
      const diagnosis = describePurchaseError(result.error);
      logEvent("error", "feature", "purchase_failed", { plan: pkg.identifier, ...diagnosis });
      // `error`-type logEvents fan out to PostHog as $exception, NOT as a product
      // event — so without this explicit capture the funnel has no failure arm and
      // purchase_started never reconciles against its outcomes. Category matches
      // what eventLogService attaches to the other events on this screen.
      track("purchase_failed", { plan: pkg.identifier, category: "feature", ...diagnosis });
      setNotice("הרכישה לא הושלמה. אפשר לנסות שוב.");
    }
    setBusy(false);
  }

  async function handleRestore() {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const result = await restorePurchases();
    if (result.ok === true && hasMembershipEntitlement(result.customerInfo)) {
      if (!settledRef.current) {
        settledRef.current = true;
        logEvent("info", "feature", "restore_completed", {});
        onPurchased?.();
        claimAndRefresh();
      }
    } else if (result.ok === false && result.error !== undefined) {
      // The store failed, so we know nothing about what this member owns — saying
      // "no previous purchases" here would be a guess presented as a fact. (A
      // restore has no store UI to cancel, so there is no cancel arm.)
      const diagnosis = describePurchaseError(result.error);
      logEvent("error", "feature", "restore_failed", diagnosis);
      // `error` logEvents reach PostHog only as $exception; see purchase_failed.
      track("restore_failed", { category: "feature", ...diagnosis });
      setNotice("לא הצלחנו לשחזר את הרכישות. בדקו את החיבור לאינטרנט ונסו שוב.");
    } else {
      setNotice("לא נמצאו רכישות קודמות.");
    }
    setBusy(false);
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" dir="rtl">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!packages || packages.length === 0) {
    return (
      <PaywallUnavailable
        onRetry={() => setOfferingAttempt((n) => n + 1)}
        onRestore={handleRestore}
        busy={busy}
        notice={notice}
      />
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-md flex-col px-6 py-8" dir="rtl">
      <header className="mb-6 text-center">
        <h2 className="mb-2 text-2xl font-bold text-foreground">הצטרפו למנוי StayYoung</h2>
        <p className="text-muted-foreground">גישה מלאה לקהילה, לשיעורים ולכל התכנים</p>
      </header>

      <div className="flex flex-col gap-3" role="radiogroup" aria-label="בחירת מסלול">
        {packages.map((pkg) => {
          const selected = pkg.identifier === selectedId;
          const perMonth = perMonthLabel(pkg);
          const saving = savingPercent(pkg);
          const isBest = pkg.identifier === "$rc_annual";
          const trial = trials[pkg.identifier];

          return (
            <button
              key={pkg.identifier}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setSelectedId(pkg.identifier)}
              className={cn(
                "relative flex items-center gap-3 rounded-xl border-2 p-4 text-start transition-colors",
                selected
                  ? "border-primary bg-primary/5"
                  : "border-border bg-card",
              )}
            >
              {/* A live free trial outranks "הכי משתלם" — it is the stronger
                  reason to pick this plan, and two badges on one card compete. */}
              {trial?.showTrial ? (
                <span className="absolute -top-2.5 end-4 rounded-full bg-emerald-600 px-2 py-0.5 text-xs font-medium text-white">
                  {trial.trialDays ? `${trial.trialDays} ימים חינם` : "תקופת ניסיון חינם"}
                </span>
              ) : (
                isBest && (
                  <span className="absolute -top-2.5 end-4 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-white">
                    הכי משתלם
                  </span>
                )
              )}

              <span
                className={cn(
                  "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
                  selected ? "border-primary bg-primary" : "border-muted-foreground/70",
                )}
              >
                {selected && <Check className="h-3 w-3 text-white" />}
              </span>

              <span className="flex-1">
                <span className="block font-semibold text-foreground">
                  {HEBREW_PERIOD[pkg.identifier] ?? pkg.product.title}
                </span>
                {perMonth && (
                  // Per-month framing makes a larger commitment legible — but the full
                  // price stays the prominent number, which Apple requires.
                  <span className="mt-0.5 block text-sm text-muted-foreground">
                    {/* "אחר כך" turns the price from what you pay now into what
                        you pay later — without it the trial badge and the price
                        contradict each other on the same card. */}
                    {trial?.showTrial && <>אחר כך </>}
                    <span dir="ltr" className="inline-block">
                      {perMonth}
                    </span>{" "}
                    לחודש
                    {saving !== null && <> · חיסכון {saving}%</>}
                  </span>
                )}
              </span>

              {/* Currency and digits must stay LTR inside an RTL block, or the ₪
                  sign renders on the wrong side of the number. */}
              <span dir="ltr" className="shrink-0 text-lg font-bold text-foreground">
                {pkg.product.priceString}
              </span>
            </button>
          );
        })}
      </div>

      {notice && (
        <p className="mt-4 text-center text-sm text-muted-foreground" role="status">
          {notice}
        </p>
      )}

      <Button size="lg" className="mt-6 w-full" disabled={busy || !selectedId} onClick={handlePurchase}>
        {busy ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : selectedTrial?.showTrial ? (
          // "המשך לתשלום" is actively wrong on a free trial — nothing is charged
          // today, and a payment-shaped button is what makes people back out.
          selectedTrial.trialDays ? `מתחילים ${selectedTrial.trialDays} ימים חינם` : "מתחילים תקופת ניסיון חינם"
        ) : (
          "המשך לתשלום"
        )}
      </Button>

      <p className="mt-3 text-center text-xs leading-relaxed text-muted-foreground">
        {selectedTrial?.showTrial ? (
          <>
            {selectedTrial.trialDays
              ? `החיוב הראשון יתבצע בתום ${selectedTrial.trialDays} ימי הניסיון. `
              : "החיוב הראשון יתבצע בתום תקופת הניסיון. "}
            אפשר לבטל בכל עת לפני כן דרך הגדרות החשבון בחנות, בלי חיוב.
          </>
        ) : (
          "המנוי מתחדש אוטומטית. אפשר לבטל בכל עת דרך הגדרות החשבון בחנות."
        )}
      </p>

      <PaywallFooterLinks onRestore={handleRestore} busy={busy} />

      {/* Last, below the legal row, so it never competes with the purchase
          decision above it — and never reads as part of the offer. §5's
          never-imply-data-loss promise, made good on the one screen where a
          member has most reason to fear it. */}
      <div className="mt-6 flex justify-center">
        <ProgressLink onOpen={() => navigate(PROGRESS_PATH)} />
      </div>
    </div>
  );
}
